// Chat Service - AWS Amplify GraphQL Integration
import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import { Observable, from, map, catchError, of } from 'rxjs';
import {
    Channel,
    ChannelFilter,
    ChannelRead,
    ChannelConnection,
    ChannelStats,
    MessageConnection,
    Message,
} from '../models/chat.models';
import {
    GET_CHANNEL_READ_BY_CHANNEL_AND_USER,
    GET_CHANNEL_READS_BY_USER_ID,
    GET_CHANNELS,
    GET_ALL_CHANNELS,
    GET_CHANNELS_BY_TYPE,
    GET_CHANNEL_STATS,
    GET_MESSAGES,
    GET_CHANNEL,
    GET_FILES,
} from '../graphql/queries';
import {
    ASSIGN_AGENT,
    CREATE_CHANNEL,
    UPDATE_CHANNEL,
    DECREMENT_UNREAD_COUNT,
    INCREMENT_UNREAD_COUNT,
    JOIN_CHANNEL,
    MARK_CHANNEL_AS_READ,
    RESET_UNREAD_COUNT,
    SEND_MESSAGE,
} from '../graphql/mutations';
import { CurrentUserModel } from '@shared/common/models';
import { UserApiService } from '@shared/common/api-services';
import { IUser } from '@shared/common/interfaces';

@Injectable({
    providedIn: 'root',
})
export class ChatService {
    private client = generateClient();
    constructor(
        private currentUser: CurrentUserModel,
        private userApiService: UserApiService,
    ) {}
    // Channel Operations
    getChannel(channelId: string): Observable<Channel | null> {
        return from(
            this.client.graphql({
                query: GET_CHANNEL,
                variables: { channelId },
            }),
        ).pipe(
            map((result: any) => result.data.getChannelById),
            catchError((error) => {
                console.error('Error fetching channel by id:', error);
                return of(null);
            }),
        );
    }
    getChannels(userId: string): Observable<Channel[]> {
        return from(
            this.client.graphql({
                query: GET_CHANNELS,
                variables: { userId },
            }),
        ).pipe(
            map((result: any) => result.data.getChannels),
            catchError((error) => {
                console.error('Error fetching channels:', error);
                return of([]);
            }),
        );
    }

    updateChannel(params: {
        channelId: string;
        name?: string;
        status?: string;
        priority?: string;
        description?: string;
        extraData?: string;
        senderAvatar?: string;
        lastSenderId?: string;
        internalNotes?: string;
        applicationStatus?: string;
    }): Observable<Channel> {
        return from(
            this.client.graphql({
                query: UPDATE_CHANNEL,
                variables: {
                    channelId: params.channelId,
                    name: params.name,
                    status: params.status,
                    priority: params.priority,
                    description: params.description,
                    extraData: params.extraData,
                    senderAvatar: params.senderAvatar,
                    lastSenderId: params.lastSenderId,
                    internalNotes: params.internalNotes,
                    applicationStatus: params.applicationStatus,
                },
            }),
        ).pipe(
            map((result: any) => result.data.updateChannel as Channel),
            catchError((error) => {
                console.error('Error updating channel:', error);
                throw error;
            }),
        );
    }

    assignAgent(channelId: string, agentId: string): Observable<Channel> {
        return from(
            this.client.graphql({
                query: ASSIGN_AGENT,
                variables: { channelId, agentId },
            }),
        ).pipe(
            map((result: any) => result.data.assignAgent),
            catchError((error) => {
                console.error('Error assigning agent:', error);
                throw error;
            }),
        );
    }

    getAllChannels(limit?: number, nextToken?: string, sortBy?: string, sortOrder?: string, filter?: ChannelFilter): Observable<ChannelConnection> {
        return from(
            this.client.graphql({
                query: GET_ALL_CHANNELS,
                variables: { limit, nextToken, sortBy, sortOrder, filter: filter ?? null },
            }),
        ).pipe(
            map((result: any) => result.data.getAllChannels),
            catchError((error) => {
                console.error('Error fetching all channels:', error);
                return of({ items: [], nextToken: undefined });
            }),
        );
    }

    getChannelsByType(types: string[], limit?: number, nextToken?: string, sortBy?: string, sortOrder?: string): Observable<ChannelConnection> {
        return from(
            this.client.graphql({
                query: GET_CHANNELS_BY_TYPE,
                variables: { type: types, limit, nextToken, sortBy, sortOrder },
            }),
        ).pipe(
            map((result: any) => result.data.getChannelsByType),
            catchError((error) => {
                console.error('Error fetching channels by type:', error);
                return of({ items: [], nextToken: undefined });
            }),
        );
    }

    getChannelStats(statuses?: string[], serviceTypes?: string[]): Observable<ChannelStats> {
        return from(
            this.client.graphql({
                query: GET_CHANNEL_STATS,
                variables: {
                    statuses: statuses?.length ? statuses : undefined,
                    serviceTypes: serviceTypes?.length ? serviceTypes : undefined,
                },
            }),
        ).pipe(
            map((result: any) => result.data.getChannelStats),
            catchError((error) => {
                console.error('Error fetching channel stats:', error);
                return of({ totalCount: 0, activeCount: 0, highPriorityCount: 0, byType: [] });
            }),
        );
    }

    createChannel(
        name: string,
        description: string,
        type: string,
        members: string[],
        referenceNo: string,
        iconName?: string,
    ): Observable<Channel> {
        return from(
            this.client.graphql({
                query: CREATE_CHANNEL,
                variables: { name, description, type, members, referenceNo, iconName },
            }),
        ).pipe(
            map((result: any) => result.data.createChannel),
            catchError((error) => {
                console.error('Error creating channel:', error);
                throw error;
            }),
        );
    }

    joinChannel(channelId: string): Observable<Channel> {
        return from(
            this.client.graphql({
                query: JOIN_CHANNEL,
                variables: { channelId },
            }),
        ).pipe(
            map((result: any) => result.data.joinChannel),
            catchError((error) => {
                console.error('Error joining channel:', error);
                throw error;
            }),
        );
    }

    // Channel Read Operations
    getChannelReadsByUserId(
        userId: string,
        limit?: number,
        nextToken?: string,
    ): Observable<{ items: ChannelRead[]; nextToken?: string }> {
        return from(
            this.client.graphql({
                query: GET_CHANNEL_READS_BY_USER_ID,
                variables: { userId, limit, nextToken },
            }),
        ).pipe(
            map((result: any) => result.data.getChannelReadsByUserId),
            catchError((error) => {
                console.error('Error fetching channel reads:', error);
                return of({ items: [] });
            }),
        );
    }

    getChannelReadByChannelAndUser(
        channelId: string,
        userId: string,
    ): Observable<ChannelRead | null> {
        return from(
            this.client.graphql({
                query: GET_CHANNEL_READ_BY_CHANNEL_AND_USER,
                variables: { channelId, userId },
            }),
        ).pipe(
            map((result: any) => result.data.getChannelReadByChannelAndUser),
            catchError((error) => {
                console.error('Error fetching channel read:', error);
                return of(null);
            }),
        );
    }

    markChannelAsRead(
        channelId: string,
        userId: string,
        lastReadMessageId: string,
    ): Observable<ChannelRead> {
        return from(
            this.client.graphql({
                query: MARK_CHANNEL_AS_READ,
                variables: { channelId, userId, lastReadMessageId },
            }),
        ).pipe(
            map((result: any) => result.data.markChannelAsRead),
            catchError((error) => {
                console.error('Error marking channel as read:', error);
                throw error;
            }),
        );
    }

    incrementUnreadCount(
        channelId: string,
        userId: string,
        increment: number,
    ): Observable<ChannelRead> {
        return from(
            this.client.graphql({
                query: INCREMENT_UNREAD_COUNT,
                variables: { channelId, userId, increment },
            }),
        ).pipe(
            map((result: any) => result.data.incrementUnreadCount),
            catchError((error) => {
                console.error('Error incrementing unread count:', error);
                throw error;
            }),
        );
    }

    decrementUnreadCount(
        channelId: string,
        userId: string,
        decrement: number,
    ): Observable<ChannelRead> {
        return from(
            this.client.graphql({
                query: DECREMENT_UNREAD_COUNT,
                variables: { channelId, userId, decrement },
            }),
        ).pipe(
            map((result: any) => result.data.decrementUnreadCount),
            catchError((error) => {
                console.error('Error decrementing unread count:', error);
                throw error;
            }),
        );
    }

    resetUnreadCount(channelId: string, userId: string): Observable<ChannelRead> {
        return from(
            this.client.graphql({
                query: RESET_UNREAD_COUNT,
                variables: { channelId, userId },
            }),
        ).pipe(
            map((result: any) => result.data.resetUnreadCount),
            catchError((error) => {
                console.error('Error resetting unread count:', error);
                throw error;
            }),
        );
    }

    getMessages(
        channelId: string,
        limit?: number,
        nextToken?: string,
    ): Observable<MessageConnection> {
        return from(
            this.client.graphql({
                query: GET_MESSAGES,
                variables: { channelId, limit, nextToken },
            }),
        ).pipe(
            map((result: any) => result.data.getMessages),
            catchError((error) => {
                console.error('Error fetching messages:', error);
                return of({ items: [], nextToken: undefined });
            }),
        );
    }

    getFiles(channelId: string, limit?: number, nextToken?: string): Observable<MessageConnection> {
        return from(
            this.client.graphql({
                query: GET_FILES,
                variables: { channelId, limit, nextToken },
            }),
        ).pipe(
            map((result: any) => result.data.getFiles),
            catchError((error) => {
                console.error('Error fetching files:', error);
                return of({ items: [], nextToken: undefined });
            }),
        );
    }

    sendMessage(params: {
        channelId: string;
        content?: string;
        files?: string[];
        isFromBot?: boolean;
    }): Observable<Message> {
        return from(
            this.client.graphql({
                query: SEND_MESSAGE,
                variables: {
                    channelId: params.channelId,
                    content: params.content,
                    files: params.files,
                    senderId: this.currentUser.cognitoId,
                    isFromBot: params.isFromBot,
                    sender: {
                        firstName: this.currentUser.firstName,
                        lastName: this.currentUser.lastName,
                        avatar: this.currentUser.profilePhotoLink,
                    },
                },
            }),
        ).pipe(
            map((res) => res.data.sendMessage),
            catchError((error) => {
                console.error('Error sending message:', error);
                throw error;
            }),
        );
    }

    getUserDetails(userId: string): Observable<IUser> {
        return this.userApiService.getUserDetails(userId).pipe(
            catchError((error) => {
                console.error('Error fetching user details:', error);
                throw error;
            }),
        );
    }
}
